import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ACQUISITION_BACKEND } from '@/constants';
import i18n from '@/translations/client-setup';
import {
    Area,
    ICity,
    ILeadRequest,
    IMakesApi_Result_SELL,
    IModelApi_Result_SELL,
    IPerspectiveLeadRequest,
    Region,
    ILeadResponse,
    IFixedLocation,
} from '@/types';

// Define our single API slice object
export const acquistionApiSlice = createApi({
    // The cache reducer expects to be added at `state.acquistion` (default is state.api - this is optional)
    reducerPath: 'acquistion',
    baseQuery: fetchBaseQuery({
        baseUrl: ACQUISITION_BACKEND,
        prepareHeaders: headers => {
            headers.set('lang', `${i18n.language}`);
            headers.set('origin', 'sylndr.com');
            headers.set('referer', 'sylndr.com');
            headers.set('Authority', 'sylndr.com');

            return headers;
        },
    }),
    tagTypes: [],
    // The "endpoints" represent operations and requests for this server
    endpoints: builder => ({
        // The getMakesV2 fetches all makes in or out of the shopping list
        getMakesV3: builder.query<IMakesApi_Result_SELL, void>({
            query: () => 'v3/shoppingList/make',
        }),
        getModelsV3: builder.query<IModelApi_Result_SELL, string>({
            query: makeId => `v3/shoppingList/model/${makeId}`,
        }),
        getFAQs: builder.query<any, string>({
            query: lang => `faqs/${lang}`,
        }),
        getFixedLocations: builder.query<IFixedLocation, void>({
            query: () => 'fixedLocations',
        }),

        getRegions: builder.query<Region[], void>({
            query: () => '/areas',
            transformResponse: (response: { data: ICity[] }) => {
                const regions = response.data;
                const formatttedRegions: Region[] = [];
                for (const city of regions) {
                    const formattedRegion: Region = {
                        id: city.cityId,
                        bigArea: city.cityEnName,
                        arBigArea: city.cityArName,
                        smallArea: city.areas.map(area => {
                            return {
                                id: area.areaId,
                                polygon: area.coordinates.map(coord => {
                                    return {
                                        lng: coord.longitude,
                                        lat: coord.latitude,
                                    };
                                }),
                                name: area.areaEnName,
                                arName: area.areaArName,
                                serviceable: area.serviceable,
                                warehouse: area.warehouse,
                            } as Area;
                        }),
                    };
                    formatttedRegions.push(formattedRegion);
                }
                return formatttedRegions;
            },
        }),
        // send otp
        sendOtp: builder.mutation<IModelApi_Result_SELL, string>({
            query: phoneNumber => ({
                url: '/sendMobileOTP',
                method: 'POST',
                body: { phoneNumber, language: i18n.language },
            }),
        }),
        // add lead
        addLead: builder.mutation<ILeadResponse, ILeadRequest>({
            query: data => ({
                url: '/v2/hTCmGnuJLjUaISKsjcSIMEFWAj',
                method: 'POST',
                body: data,
            }),
        }),
        // add perspective lead
        addPerspectiveLead: builder.mutation<
            ILeadResponse,
            IPerspectiveLeadRequest
        >({
            query: data => ({
                url: '/v3/verifyFutureLeads',
                method: 'POST',
                body: data,
            }),
        }),
    }),
});

// Export the auto-generated hooks
export const {
    useLazyGetMakesV3Query,
    useLazyGetModelsV3Query,
    useLazyGetRegionsQuery,
    useAddLeadMutation,
    useAddPerspectiveLeadMutation,
    useSendOtpMutation,
    useGetFAQsQuery,
    useGetFixedLocationsQuery,
} = acquistionApiSlice;
